import React, { useState, useMemo, useEffect } from 'react';
import { HelpCircle, Search, X } from 'lucide-react';

/**
 * Catalog of features in the app. Each entry:
 *   { id, title, where, steps, tab, keywords }
 * `tab` can optionally match an activeTab value so clicking the entry opens that tab.
 */
const FEATURES = [
  {
    id: 'schedule-blocking-reminder',
    title: 'Tids-påminnelse som låser appen tills du bockar av',
    where: 'Tider 🔔-fliken',
    tab: 'schedule',
    steps: [
      'Gå till "Tider 🔔"-fliken',
      'Klicka "Lägg till påminnelse"',
      'Skriv vad: t.ex. "Lägg in kläder i tvättmaskinen"',
      'Sätt klockslag: 21:00',
      'Välj vilka dagar (varje dag, vardagar, etc)',
      'Slå PÅ "🔒 Låser appen tills den är klar"-knappen (röd)',
      'Spara — kl 21:00 dyker det upp en kawaii-fullskärm-popup som inte stängs förrän du bockar av uppgiften',
      'Tips: Vill du bara en mjuk påminnelse utan lås — slå AV den röda knappen',
    ],
    keywords: ['påminnelse', 'tid', 'klockslag', 'låsa', 'tvätt', 'medicin', 'måste', 'kryssa', 'bocka av', 'reminder', 'schedule', 'låser appen'],
  },
  {
    id: 'cleaning-qr-print',
    title: 'Skriv ut QR-kod för en städuppgift',
    where: 'Städ-fliken',
    tab: 'cleaning',
    steps: [
      'Gå till Städ-fliken',
      'Hitta uppgiften i listan',
      'Klicka på lila "QR"-knappen till höger om uppgiften',
      'Klicka "Skriv ut" i modalen',
    ],
    keywords: ['qr', 'kod', 'skriv ut', 'städ', 'print', 'städuppgift'],
  },
  {
    id: 'cleaning-qr-all',
    title: 'Skriv ut ALLA QR-koder på A4-ark',
    where: 'Städ-fliken, längst ner',
    tab: 'cleaning',
    steps: [
      'Gå till Städ-fliken',
      'Scrolla till "Alla uppgifter" i botten',
      'Klicka "Skriv ut alla QR (N)"',
    ],
    keywords: ['qr', 'alla', 'skriv ut', 'a4', 'sheet', 'samling'],
  },
  {
    id: 'cleaning-reference-image',
    title: 'Lägg till referensbild på en städuppgift',
    where: 'Städ-fliken',
    tab: 'cleaning',
    steps: [
      'Gå till Städ-fliken',
      'Klicka "Ändra" (pennikonen) bredvid uppgiften',
      'Klicka "Välj bild" under "Referensbild"',
      'Välj bild från kamera eller galleri',
      'Klicka "Spara"',
    ],
    keywords: ['bild', 'städ', 'referens', 'foto', 'image', 'inspiration'],
  },
  {
    id: 'room-analyze',
    title: 'Analysera ett rum/låda för ADHD-tips',
    where: 'Mitt Rum-fliken',
    tab: 'rooms',
    steps: [
      'Gå till Mitt Rum-fliken',
      'Klicka "Välj bild från kamera / galleri"',
      'Skriv vad det är (valfritt)',
      'Klicka "Analysera rummet"',
      'Läs förslagen och bocka av dem du fixar',
    ],
    keywords: ['rum', 'bild', 'foto', 'låda', 'ai', 'analys', 'förslag', 'organisera'],
  },
  {
    id: 'room-visualize',
    title: 'Visualisera hur rummet SKULLE kunna se ut',
    where: 'Mitt Rum-fliken → öppna ett rum',
    tab: 'rooms',
    steps: [
      'Öppna ett analyserat rum',
      'Klicka "Visa hur det SKULLE se ut ✨"',
      'Vänta 15-25 sek',
      'Dra i skjutreglaget för att se före/efter',
    ],
    keywords: ['rum', 'visualisera', 'före', 'efter', 'ai', 'nano banana', 'filter'],
  },
  {
    id: 'room-storage',
    title: 'Hitta förvaringen som syns i efterbilden — köp eller spara',
    where: 'Mitt Rum-fliken → öppna ett rum med efterbild',
    tab: 'rooms',
    steps: [
      'Öppna ett rum där du redan skapat efterbilden',
      'Klicka "Visa förvaring" i avsnittet "🛍️ Förvaring i bilden"',
      'AI:n identifierar 4-8 produkter (korgar, hyllor, krokar etc) som syns',
      'Klicka butiksknappen (IKEA, JYSK, Granit, Lagerhaus, Tradera, Sellpy) för att hitta produkten',
      'Klicka "💖 Spara i Garderob" — produkten läggs in i din önskelista under Hemsaker',
    ],
    keywords: ['förvaring', 'shopping', 'köpa', 'korg', 'hylla', 'ikea', 'jysk', 'granit', 'lagerhaus', 'tradera', 'sellpy', 'hemsaker', 'önskelista'],
  },
  {
    id: 'cleaning-routines',
    title: 'Morgon- och kvällsrutin för städ',
    where: 'Städ-fliken, högst upp',
    tab: 'cleaning',
    steps: [
      'Gå till Städ-fliken',
      'Klicka "Morgon-rutin" eller "Kväll-rutin"',
      'Gör uppgifterna i tur och ordning',
    ],
    keywords: ['rutin', 'morgon', 'kväll', 'städ', 'routine', 'chain'],
  },
  {
    id: 'pet-feed',
    title: 'Mata och leka med husdjuret',
    where: 'Högst upp på Dashboard',
    steps: [
      'Scrolla till toppen av Dashboard',
      'Klicka "Feed" eller "Play" bredvid husdjuret',
    ],
    keywords: ['pet', 'husdjur', 'mata', 'lek', 'kompis'],
  },
  {
    id: 'pet-voice',
    title: 'Byt röst på kawaii-kompisen',
    where: 'Header längst upp',
    steps: [
      'Klicka "Vanlig röst" i headern',
      'Välj en av 6 djurröster',
    ],
    keywords: ['röst', 'ljud', 'tts', 'djur', 'voice', 'uppläsning'],
  },
  {
    id: 'dictaphone',
    title: 'Diktera text med röst (Dictaphone)',
    where: 'Flytande knapp nere till vänster',
    steps: [
      'Klicka på mikrofon-ikonen nere till vänster',
      'Tala din text',
      'Klicka stopp och kopiera eller använd texten',
    ],
    keywords: ['diktera', 'tal', 'röst', 'mikrofon', 'dictate', 'speech'],
  },
  {
    id: 'calendar-work',
    title: 'Lägga in jobbpass ("Dagens jobb")',
    where: 'Kalender-fliken',
    tab: 'calendar',
    steps: [
      'Gå till Kalender',
      'Hitta "Dagens jobb" snabbknapp',
      'Sök arbetsplats eller skriv ny',
      'Bekräfta tid',
    ],
    keywords: ['jobb', 'pass', 'kalender', 'arbete', 'skift', 'shift'],
  },
  {
    id: 'episode',
    title: 'När det kryper — stöd vid skov (hypomani/ångest/tunga perioder)',
    where: 'Hjärtikonen uppe i headern',
    steps: [
      'Klicka på "När det kryper"-knappen uppe i headern (rosa-lila gradient med hjärta)',
      'Välj vad som känns just nu (Det kryper / Ångest / Irritabel / Tungt / Blandat / Utmattad)',
      'Skala intensitet 1-5',
      'Guidas genom: Dumpa det → Grounding 5-4-3-2-1 → Bränn av energi → Snälla ord',
      'Skovet loggas automatiskt så du ser mönster över tid',
    ],
    keywords: ['skov', 'hypomani', 'ångest', 'panik', 'nedstämd', 'kryper', 'babblig', 'rastlös', 'kris', 'stöd'],
  },
  {
    id: 'focus-lock',
    title: 'Fokus med låsläge + pausövningar',
    where: 'Fokusera-fliken',
    tab: 'focus',
    steps: [
      'Gå till Fokusera',
      'Aktivera "🔒 Låsläge"-kryssrutan',
      'Starta en Pomodoro (25 min) eller annan timer',
      'Din kompis går in i fullskärm — håll ut, det är bara 25 min 💛',
      'När tiden är slut får du 5 pausövningar ledda av kawaii-djur',
      'Tryck & håll 1.5 sekunder på nöd-knappen om du verkligen måste avbryta',
    ],
    keywords: ['fokus', 'pomodoro', 'timer', 'lås', 'koncentration', 'paus', 'övning', 'stretch', 'vila'],
  },
  {
    id: 'makeup',
    title: 'Sminkspegel — personlig sminkguide från selfie',
    where: 'Sminkspegel-fliken',
    tab: 'makeup',
    steps: [
      'Gå till Sminkspegel',
      'Ta en selfie eller välj från album',
      'Välj en stil (Naturlig vardag, Glow, Kväll, m.m.)',
      'Skriv önskemål om du vill (valfritt)',
      'Få AI-förhandsvisning + personlig steg-för-steg-guide + produktförslag',
    ],
    keywords: ['smink', 'makeup', 'selfie', 'ansikte', 'filter', 'ögon', 'läppar', 'bryn', 'rouge'],
  },
  {
    id: 'outfit',
    title: 'Klädarketyp + outfit-förslag (i Sminkspegel)',
    where: 'Sminkspegel → öppna en sminkguide',
    tab: 'makeup',
    steps: [
      'Gå till Sminkspegel',
      'Öppna en sparad sminkguide (eller skapa en ny)',
      'Scrolla ner och klicka "Få klädarketyp + outfit-förslag"',
      'Valfritt: ladda upp en helkroppsbild + skriv preferenser',
      'Klicka "Skapa outfit-förslag" — få arketyp + 4 outfits som matchar din sminkning',
    ],
    keywords: ['kläder', 'outfit', 'stil', 'arketyp', 'klädstil', 'garderob', 'klädtips'],
  },
  {
    id: 'wardrobe',
    title: 'Garderob — Pinterest-stil kapselgarderob',
    where: 'Garderob-fliken',
    tab: 'wardrobe',
    steps: [
      'Gå till Garderob (👗-ikon)',
      'Klicka "Lägg till" för att lägga in plagg du önskar',
      'Eller klicka "Importera från sminkguide" för att hämta alla outfit-förslag du sparat',
      'Klicka på ett plagg för att söka det hos H&M, Lyko, Lindex, Tradera m.fl.',
      'Markera "Köpt" när du har plagget hemma — så följer du din egen kapselgarderob',
      'Filter "Hemsaker" — visa förvaring du sparat från Mitt Rum',
    ],
    keywords: ['garderob', 'kläder', 'pinterest', 'kapsel', 'köpt', 'önskelista', 'shopping', 'plagg', 'outfit', 'hemsaker', 'förvaring'],
  },
  {
    id: 'timetravel',
    title: 'Tidsresa — färdas i tiden och möt historiska personer',
    where: 'Tidsresa-fliken',
    tab: 'timetravel',
    steps: [
      'Gå till Tidsresa (🕰️-ikon)',
      'Beskriv vart du vill (t.ex. "Columbus däck 1492") eller välj en epok-förinställning',
      'Vill du bli överraskad? Klicka "Hemlig epok" för en slumpad resa',
      'Läs de 3 fakta du får innan resan börjar',
      'Prata med karaktären på svenska — de svarar i karaktär från sin tid',
      'Klicka "Visa hur det ser ut här" för att få en AI-genererad scenbild',
      'Klicka "En dag i livet" för att låta karaktären berätta morgon → kväll i 5 kapitel med scenbilder + uppläsning',
      'Klicka "Gå runt här" för att utforska 4-6 platser med olika personer du kan prata med (t.ex. Spegelsalen, Trädgården, kapellet — varje plats med 2-3 NPCs som du kan klicka och chatta med)',
      'Alla resor sparas så du kan återvända senare',
    ],
    keywords: ['tidsresa', 'historia', 'rollspel', 'columbus', 'vikingar', 'medeltid', 'kleopatra', 'färdas', 'tiden', 'epok', 'historisk', 'dag i livet', 'berätta', 'uppläsning', 'utforska', 'gå runt', 'spegelsalen', 'rum', 'platser', 'människor'],
  },
  {
    id: 'diary',
    title: 'Kära dagbok — skriv och få ett varmt svar',
    where: 'Kära dagbok-fliken',
    tab: 'diary',
    steps: [
      'Gå till Kära dagbok (hjärta-ikon)',
      'Välj vilken röst du behöver (Mamma, Bästis, Klok vän, Bara lyssna, Peppa)',
      'Skriv vad du känner — eller diktera med mikrofonen',
      'Tryck "Skicka" — svaret visas direkt och sparas',
      'Bläddra i dina tidigare inlägg när du vill',
    ],
    keywords: ['dagbok', 'mamma', 'chatt', 'braindump', 'känslor', 'stöd', 'kompis', 'lyssna'],
  },
  {
    id: 'memories-record',
    title: 'Spela in privat minnesvideo',
    where: 'Minnen-fliken',
    tab: 'memories',
    steps: [
      'Gå till Minnen-fliken',
      'Klicka "Spela in"',
      'Välj filter (VHS, Polaroid, Super 8 m.fl.)',
      'Tryck röda knappen – spela in upp till 60 sek',
      'Lägg till bildtext, datum och taggar (t.ex. barnens namn)',
      'Spara i ditt privata album',
    ],
    keywords: ['video', 'film', 'minnen', 'kamera', 'spela in', 'tiktok', 'snapchat', 'filter', 'polaroid', 'vhs', 'barnen'],
  },
  {
    id: 'memories-upload',
    title: 'Ladda upp befintlig video till Minnen',
    where: 'Minnen-fliken',
    tab: 'memories',
    steps: [
      'Gå till Minnen-fliken',
      'Klicka "Ladda upp"',
      'Välj video från galleri (max 60 MB)',
      'Välj filter och spara',
    ],
    keywords: ['video', 'ladda upp', 'galleri', 'minnen', 'import'],
  },
  {
    id: 'memories-yearbook',
    title: 'Årsbok — spela upp alla klipp från ett år',
    where: 'Minnen-fliken, "Årsbok"-knappen',
    tab: 'memories',
    steps: [
      'Gå till Minnen',
      'Klicka "Årsbok" (gul knapp)',
      'Välj år i rullgardinen',
      'Filmen spelas automatiskt med titelkort mellan månaderna',
    ],
    keywords: ['årsbok', 'recap', 'film', 'sammanställning', 'slide', 'år'],
  },
  {
    id: 'memories-growth',
    title: 'Tillväxtalbum — alla klipp per barn/tagg',
    where: 'Minnen-fliken, på varje tagg',
    tab: 'memories',
    steps: [
      'Gå till Minnen',
      'Se tagg-raden under "Filtrera eller öppna tillväxtalbum"',
      'Klicka på bok-ikonen bredvid ett namn',
      'Se alla klipp för det barnet, grupperat per år',
    ],
    keywords: ['tillväxt', 'album', 'barn', 'tagg', 'tilde', 'maja'],
  },
  {
    id: 'reading-log',
    title: 'Logga läsning och böcker',
    where: 'Läslogg-fliken',
    tab: 'reading',
    steps: [
      'Gå till Läslogg',
      'Klicka "Lägg till bok" eller sök via Google Books',
      'Uppdatera sidor dagligen',
    ],
    keywords: ['bok', 'läs', 'reading', 'läslogg', 'hatchi'],
  },
  {
    id: 'notifications',
    title: 'Browser-notifikationer (morgon, kväll, vatten)',
    where: 'Ikonen uppe i headern',
    steps: [
      'Godkänn notifikationer första gången',
      'Du får påminnelser automatiskt 07:30, 21:00 + vatten varje timme',
    ],
    keywords: ['notif', 'påminnelse', 'vatten', 'morgon', 'kväll', 'alert'],
  },
  {
    id: 'post-its',
    title: 'Skapa post-it-lappar',
    where: 'Högst upp på Dashboard',
    steps: [
      'Klicka "+"-knappen i post-it-raden',
      'Skriv text',
      'Dra för att ordna om',
    ],
    keywords: ['postit', 'post-it', 'lapp', 'snabbanteckning'],
  },
  {
    id: 'forgot-password',
    title: 'Återställa lösenord via mejl',
    where: 'Inloggningssidan',
    steps: [
      'Klicka "Forgot password?" på login',
      'Skriv din mejl',
      'Kolla mejlen för återställningslänk',
    ],
    keywords: ['lösenord', 'reset', 'glömt', 'mejl', 'email'],
  },
];

export default function GlobalHelp({ onOpenTab }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
      if ((e.key === '?' && (e.shiftKey || e.metaKey)) || (e.key === '/' && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FEATURES;
    return FEATURES.filter((f) =>
      f.title.toLowerCase().includes(q) ||
      f.where.toLowerCase().includes(q) ||
      f.keywords.some((k) => k.includes(q))
    );
  }, [query]);

  const goto = (f) => {
    if (f.tab && onOpenTab) onOpenTab(f.tab);
    setOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="neo-btn px-2 py-1.5 bg-[#FFF0F5] flex items-center gap-1 text-xs font-bold"
        title="Hjälp — hitta allt i appen (Skift+?)"
        data-testid="global-help-btn"
        aria-label="Öppna hjälp"
      >
        <HelpCircle className="w-4 h-4" />
        <span className="hidden sm:inline">Hjälp</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="global-help-modal"
        >
          <div
            className="neo-card bg-white w-full max-w-lg p-4 my-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <HelpCircle className="w-5 h-5" /> Hitta allt i appen
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="neo-btn p-1.5 bg-white"
                aria-label="Stäng"
                data-testid="global-help-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/50" />
              <input
                autoFocus
                type="text"
                placeholder="Sök t.ex. qr, bild, rum, jobb..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="neo-input w-full pl-9"
                data-testid="global-help-search"
              />
            </div>

            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {filtered.length === 0 ? (
                <p className="text-center py-6 text-sm text-black/60">Inget hittades. Prova ett annat ord!</p>
              ) : (
                filtered.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => goto(f)}
                    className="w-full neo-card p-3 bg-[#FDF0D5] hover:bg-[#FFE8A3] text-left transition"
                    data-testid={`help-item-${f.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-bold text-sm leading-tight">{f.title}</h4>
                      {f.tab && (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border-[2px] border-black bg-white shrink-0">
                          Öppna
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-black/70 mb-1">📍 {f.where}</p>
                    <ol className="text-[11px] text-black/80 list-decimal list-inside space-y-0.5">
                      {f.steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  </button>
                ))
              )}
            </div>
            <p className="text-[10px] text-black/50 mt-3 text-center">
              Tips: tryck <kbd className="border border-black/30 rounded px-1">Skift</kbd>+<kbd className="border border-black/30 rounded px-1">?</kbd> för att öppna hjälpen var som helst
            </p>
          </div>
        </div>
      )}
    </>
  );
}
